import { useCallback, useEffect, useMemo, useState } from 'react'
import { AccountLink, DrillValue } from '../components/Drill'
import { useTableControls, type Column } from '../lib/useTableControls'
import { TableHead } from '../components/TableControls'
import { DetailGrid, ExpandableRow, useOpenRow } from '../components/RowDetail'
import {
  getAudit,
  downloadAuditExport,
  type AuditEvent,
  type AuditIntegrity,
  type AuditQuery
} from '../lib/adminClient'

// Quick action-prefix chips. The `action` filter is a server-side prefix match,
// so an empty value means "all actions" and "admin." matches every admin action.
const ACTION_CHIPS: Array<{ label: string; value: string }> = [
  { label: 'All actions', value: '' },
  { label: 'auth.', value: 'auth.' },
  { label: 'admin.', value: 'admin.' },
  { label: 'member.', value: 'member.' },
  { label: 'org.', value: 'org.' }
]

// A datetime-local string (local time, no zone) to unix ms, or undefined when
// empty. The input gives us "YYYY-MM-DDTHH:mm"; Date parses that as local time.
function localToMs(v: string): number | undefined {
  if (!v) return undefined
  const t = new Date(v).getTime()
  return Number.isFinite(t) ? t : undefined
}

// Absolute UTC timestamp for the table. The log is a compliance record, so an
// exact time reads better here than a relative "5m ago".
function fmtTime(ms: number): string {
  return new Date(ms).toISOString().slice(0, 19).replace('T', ' ') + ' UTC'
}

// Render `detail` compactly. It arrives as a JSON string or plain text; when it
// parses as JSON we pretty-print it, otherwise we show it verbatim.
function formatDetail(detail: string | null): string {
  if (!detail) return ''
  try {
    return JSON.stringify(JSON.parse(detail), null, 2)
  } catch {
    return detail
  }
}

function IntegrityBanner({ integrity }: { integrity: AuditIntegrity }): JSX.Element {
  if (integrity.ok) {
    return (
      <div className="rounded-xl px-4 py-3 mb-4 border border-emerald-500/25 bg-emerald-500/10 text-emerald-200 text-sm flex items-center gap-3">
        <span className="text-accent">◆</span>
        <span>
          Log verified, {integrity.checked.toLocaleString()}{' '}
          {integrity.checked === 1 ? 'event' : 'events'} checked, tamper-evident.
        </span>
      </div>
    )
  }
  return (
    <div className="rounded-xl px-4 py-3 mb-4 border border-red-500/30 bg-red-500/10 text-red-200 text-sm flex items-center gap-3">
      <span className="text-red-300">▲</span>
      <span>
        Integrity check failed
        {integrity.brokenAt ? ` at ${integrity.brokenAt}` : ''}
        {integrity.reason ? `. ${integrity.reason}` : '.'} The chain does not verify,
        so events may have been altered or removed.
      </span>
    </div>
  )
}

const AUDIT_COLUMNS: Column<AuditEvent>[] = [
  { key: 'time', label: 'Time', value: (e) => e.createdAt, numeric: true },
  { key: 'action', label: 'Action', value: (e) => e.action },
  { key: 'actor', label: 'Actor', value: (e) => e.actorAccountId ?? '', filter: 'text' },
  { key: 'target', label: 'Target', value: (e) => `${e.targetType ?? ''} ${e.targetId ?? ''}`.trim(), filter: 'text' },
  { key: 'ip', label: 'IP', value: (e) => e.ip ?? '', filter: 'text' },
  { key: 'detail', label: 'Detail', value: (e) => e.detail ?? '', filter: 'text' }
]

export default function AuditLog(): JSX.Element {
  // Draft filter inputs the operator edits, and the applied query the fetch runs.
  const [actionPrefix, setActionPrefix] = useState('')
  const [actor, setActor] = useState('')
  const [orgId, setOrgId] = useState('')
  const [since, setSince] = useState('')
  const [until, setUntil] = useState('')

  const [events, setEvents] = useState<AuditEvent[]>([])
  // The server already narrows by its own query; this sorts and filters what
  // came back, which is what an operator reaches for mid-investigation.
  const t = useTableControls(events, AUDIT_COLUMNS, 'audit')
  const [integrity, setIntegrity] = useState<AuditIntegrity | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const rows = useOpenRow()
  const [exporting, setExporting] = useState<'csv' | 'json' | null>(null)
  const [exportError, setExportError] = useState<string | null>(null)

  const query: AuditQuery = useMemo(
    () => ({
      action: actionPrefix.trim() || undefined,
      actor: actor.trim() || undefined,
      orgId: orgId.trim() || undefined,
      since: localToMs(since),
      until: localToMs(until),
      limit: 500
    }),
    [actionPrefix, actor, orgId, since, until]
  )

  const load = useCallback(async () => {
    setState('loading')
    setError(null)
    const res = await getAudit(query)
    if (res.ok) {
      setEvents(res.data.events)
      setIntegrity(res.data.integrity)
      setState('ready')
    } else {
      setError(res.error)
      setState('error')
    }
  }, [query])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const res = await getAudit(query)
      if (cancelled) return
      if (res.ok) {
        setEvents(res.data.events)
        setIntegrity(res.data.integrity)
        setState('ready')
      } else {
        setError(res.error)
        setState('error')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [query])

  async function runExport(format: 'csv' | 'json'): Promise<void> {
    setExporting(format)
    setExportError(null)
    const res = await downloadAuditExport(query, format)
    if (!res.ok) setExportError(`Export failed: ${res.error}`)
    setExporting(null)
  }

  function resetFilters(): void {
    setActionPrefix('')
    setActor('')
    setOrgId('')
    setSince('')
    setUntil('')
  }

  return (
    <div>
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Compliance</div>
          <h1 className="text-2xl font-semibold mt-1">
            Audit log
            <span className="text-slate-400 text-base ml-2 font-normal">
              {events.length} {events.length === 1 ? 'event' : 'events'}
            </span>
          </h1>
          <div className="text-sm text-slate-400 mt-1">
            Hash-chained trail of admin and account actions. Newest first.
          </div>
        </div>
        <div className="flex gap-2">
          <button
            className="btn-ghost text-sm"
            onClick={() => void runExport('csv')}
            disabled={exporting !== null || state === 'loading'}
          >
            {exporting === 'csv' ? 'Exporting…' : 'Export CSV'}
          </button>
          <button
            className="btn-ghost text-sm"
            onClick={() => void runExport('json')}
            disabled={exporting !== null || state === 'loading'}
          >
            {exporting === 'json' ? 'Exporting…' : 'Export JSON'}
          </button>
        </div>
      </div>

      {integrity && <IntegrityBanner integrity={integrity} />}
      {exportError && (
        <div className="mb-4 text-xs text-red-300 bg-red-500/5 border border-red-500/15 rounded px-3 py-2">
          {exportError}
        </div>
      )}

      <div className="glass-card rounded-xl p-4 mb-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {ACTION_CHIPS.map((chip) => {
            const active = actionPrefix === chip.value
            return (
              <button
                key={chip.label}
                onClick={() => setActionPrefix(chip.value)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  active
                    ? 'border-accent bg-accent/10 text-white'
                    : 'border-white/10 text-slate-300 hover:bg-white/5'
                }`}
              >
                {chip.label}
              </button>
            )
          })}
        </div>
        <div className="flex flex-wrap gap-3">
          <input
            value={actionPrefix}
            onChange={(e) => setActionPrefix(e.target.value)}
            placeholder="Action prefix, e.g. admin.user"
            className="flex-1 min-w-[200px] bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent"
          />
          <input
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            placeholder="Actor account id"
            className="flex-1 min-w-[200px] bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent"
          />
          <input
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            placeholder="Org id"
            className="flex-1 min-w-[160px] bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent"
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-[11px] uppercase tracking-wider text-slate-400">Since</label>
          <input
            type="datetime-local"
            value={since}
            onChange={(e) => setSince(e.target.value)}
            className="bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent"
          />
          <label className="text-[11px] uppercase tracking-wider text-slate-400">Until</label>
          <input
            type="datetime-local"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            className="bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent"
          />
          <div className="ml-auto flex gap-2">
            <button className="btn-ghost text-xs" onClick={resetFilters}>
              Clear
            </button>
            <button className="btn-ghost text-xs" onClick={() => void load()}>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {state === 'loading' && (
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400">Loading audit log…</div>
      )}
      {state === 'error' && (
        <div className="glass-card rounded-xl p-6 text-sm text-red-300">
          Could not load the audit log: {error}
        </div>
      )}
      {state === 'ready' && events.length === 0 && (
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400">
          No events match these filters.
        </div>
      )}
      {state === 'ready' && events.length > 0 && (
        <div className="glass-card rounded-xl overflow-hidden">
          <table className="admin-table">
            <TableHead t={t} />
            <tbody>
              {t.rows.map((ev) => {
                const isOpen = rows.isOpen(ev.id)
                const detail = formatDetail(ev.detail)
                return (
                  <ExpandableRow
                    key={ev.id}
                    open={isOpen}
                    onToggle={() => rows.toggle(ev.id)}
                    colSpan={AUDIT_COLUMNS.length}
                    testId={`audit-row-${ev.id}`}
                    detail={() => (
                      <DetailGrid
                        facts={[
                          ['When', fmtTime(ev.createdAt)],
                          ['Action', <span className="font-mono">{ev.action}</span>],
                          ['Actor account', ev.actorAccountId ? <AccountLink id={ev.actorAccountId}><span className="font-mono break-all">{ev.actorAccountId}</span></AccountLink> : 'None (the system, or before sign-in)'],
                          ['Organisation', ev.orgId ? <DrillValue to={{ set: 'orgs', filters: { id: ev.orgId } }} label={`Organisation ${ev.orgId}`}><span className="font-mono break-all">{ev.orgId}</span></DrillValue> : null],
                          ['Target kind', ev.targetType],
                          [
                            'Target id',
                            !ev.targetId ? null : ev.targetType === 'account' ? (
                              <AccountLink id={ev.targetId}><span className="font-mono break-all">{ev.targetId}</span></AccountLink>
                            ) : ev.targetType === 'org' ? (
                              <DrillValue to={{ set: 'orgs', filters: { id: ev.targetId } }} label={`Organisation ${ev.targetId}`}><span className="font-mono break-all">{ev.targetId}</span></DrillValue>
                            ) : (
                              <span className="font-mono break-all">{ev.targetId}</span>
                            )
                          ],
                          ['IP address', ev.ip ? <span className="font-mono">{ev.ip}</span> : null],
                          ['Event id', <span className="font-mono break-all">{ev.id}</span>]
                        ]}
                        wide={[
                          ['Detail', detail ? <pre className="bg-black/40 border border-white/5 rounded-md p-3 overflow-x-auto font-mono whitespace-pre-wrap break-all">{detail}</pre> : 'No detail recorded.'],
                          ['Browser or app (user agent)', ev.userAgent],
                          ['Hash (this event)', <span className="font-mono break-all">{ev.hash}</span>],
                          ['Hash of the event before (chains the log)', ev.prevHash ? <span className="font-mono break-all">{ev.prevHash}</span> : 'None: the first event in the chain']
                        ]}
                      />
                    )}
                  >
                    <td className="whitespace-nowrap text-slate-300 tabular-nums">
                      {fmtTime(ev.createdAt)}
                    </td>
                    <td>
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-slate-200 font-mono">
                        {ev.action}
                      </span>
                    </td>
                    <td className="text-slate-300 font-mono text-[11px]">
                      {ev.actorAccountId ? `${ev.actorAccountId.slice(0, 16)}…` : '—'}
                    </td>
                    <td className="text-slate-300 text-[11px]">
                      {ev.targetType ? (
                        <span>
                          <span className="text-slate-400">{ev.targetType}</span>
                          {ev.targetId ? (
                            <span className="font-mono"> · {ev.targetId.slice(0, 16)}…</span>
                          ) : null}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="text-slate-400 font-mono text-[11px]">{ev.ip ?? '—'}</td>
                    <td className="text-slate-400 text-[11px] max-w-[220px] truncate">
                      {detail ? (isOpen ? 'Hide' : detail) : '—'}
                    </td>
                  </ExpandableRow>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
